import React, {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { FACheckboxInput } from "@impero-com/common/components/form/formAtomFields/faCheckboxInput";
import { FAStringInput } from "@impero-com/common/components/form/formAtomFields/faStringInput";
import { formAtom } from "@impero-com/common/utils/form/formAtom";
import {
    useCompoundFormState,
    useFormAtom,
} from "@impero-com/common/utils/form/formAtom/hooks";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { uuidRegEx } from "@impero-com/common/utils/uuid";
import { Button, Svg } from "@impero-com/ui";
import { TextAreaRef } from "antd/es/input/TextArea";
import { cx } from "class-variance-authority";
import { atom, atom as createAtom, SetStateAction, useAtomValue } from "jotai";
import { focusAtom } from "jotai-optics";
import * as Yup from "yup";

import { SavedBlob } from "api/generated/model";
import { Drawer } from "components/drawer";
import { FieldCondition, Field } from "components/fieldBuilder/models";
import { FAFileUploaderInput } from "components/form/formAtomFields/faFileUploaderInput";
import { FAMarkdownInput } from "components/form/formAtomFields/faMarkdownInput";
import DeleteIcon from "img/delete.svg";
import CopyIcon from "img/duplicate.svg";
import { CONSTANTS } from "utils/constants";

import { BlobData, mapBlobDataToUploaderFiles } from "./blobMapping";
import { ConditionEditor } from "./conditionEditor";
import { CompoundFormForwardedRef, CompoundFormRef } from "./fieldBuilder";
import { FieldDrawerSeparator } from "./fieldDrawerSeparator";
import * as theme from "./theme.css";

export interface DrawerBehavior {
    hideRequired: boolean;
    cannotDelete: boolean;
    cannotCopy: boolean;
    hideCondition: boolean;
}

export const DEFAULT_DRAWER_BEHAVIOR: DrawerBehavior = {
    hideRequired: false,
    cannotDelete: false,
    cannotCopy: false,
    hideCondition: false,
};

export interface FieldDrawerState<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
> {
    fieldAtom: FormAtom<F>;
}

export type FieldDrawerType<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
> =
    | { type: "READ_ONLY" }
    | {
          type: "EDIT";
          onDeleteField: (fieldAtom: FormAtom<F>) => void;
          onCopyField: (fieldAtom: FormAtom<F>) => Promise<void>;
      };

interface Atoms {
    nameAtom: FormAtom<string>;
    instructionsAtom: FormAtom<string>;
    conditionAtom: FormAtom<FieldCondition<string> | null>;
    requiredAtom: FormAtom<boolean>;
    blobIdsAtom: FormAtom<string[]>;
}

interface CommonDrawerProps<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
> {
    blobsAtom: FormAtom<SavedBlob[]>;
    renderFieldDrawerCustomSection: (
        fieldAtom: FormAtom<F>,
        ref: CompoundFormForwardedRef
    ) => React.ReactNode;
    disabled?: boolean;
    getFieldTypeConditionalValues: (fieldType: FT) => Array<string>;
}

interface FieldDrawerProps<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
> extends CommonDrawerProps<F, FT, FTN> {
    state: FieldDrawerState<F, FT, FTN>;
    type: FieldDrawerType<F, FT, FTN>;
    onClose: () => void;
    fieldAtoms: Array<FormAtom<F>>;
    getFieldTypeDrawerBehavior: (fieldType: FT) => DrawerBehavior;
    open: boolean;
    renderFieldDrawerHeader: (fieldAtom: FormAtom<F>) => React.ReactNode;
}
function FieldDrawerNoMemo<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
>({
    type,
    state,
    fieldAtoms,
    renderFieldDrawerHeader,
    renderFieldDrawerCustomSection,
    getFieldTypeConditionalValues,
    getFieldTypeDrawerBehavior,
    onClose,
    disabled = false,
    blobsAtom,
    open,
}: FieldDrawerProps<F, FT, FTN>) {
    const [drawerBusy, setDrawerBusy] = useState<boolean>(false);
    const customFormRef = useRef<CompoundFormRef>(null);

    const { fieldAtom } = state;

    const atoms = useMemo(() => {
        return {
            nameAtom: formAtom<string>(
                focusAtom(fieldAtom, (field) => field.prop("title")),
                Yup.string()
                    .defined()
                    .required(t("FieldBuilder_Error_Field_Name_Required"))
                    .max(CONSTANTS.FIELD_CONSTANTS.MAX_FIELD_TITLE_LENGTH)
            ),
            instructionsAtom: formAtom<string>(
                focusAtom(fieldAtom, (field) => field.prop("instructions")),
                Yup.string().defined()
            ),
            conditionAtom: formAtom<FieldCondition<string> | null>(
                focusAtom(fieldAtom, (field) => field.prop("condition")),
                Yup.mixed<FieldCondition<string>>().defined().nullable()
            ),
            requiredAtom: formAtom<boolean>(
                focusAtom(fieldAtom, (field) => field.prop("mandatory")),
                Yup.boolean().defined()
            ),
            blobIdsAtom: formAtom<string[]>(
                focusAtom(fieldAtom, (field) => field.prop("blobIds")),
                Yup.array()
                    .defined()
                    .of(
                        Yup.string()
                            .defined()
                            .matches(uuidRegEx, {
                                name: "uuid",
                                message: t("Form_Error_Types_Regex", {
                                    field: "blobIdsAtom",
                                    type: "UUID",
                                }),
                                excludeEmptyString: false,
                            })
                    )
            ),
        };
    }, [fieldAtom]);

    // Condition error above may be fixed by moving things around, so it is not included here.
    const allAtoms = useMemo(() => {
        return {
            nameAtom: atoms.nameAtom,
            instructionsAtom: atoms.instructionsAtom,
            conditionAtom: atoms.conditionAtom,
            requiredAtom: atoms.requiredAtom,
        };
    }, [atoms]);

    const { validate: validateForm } = useCompoundFormState(
        allAtoms,
        fieldAtom
    );
    const { error, value } = useFormAtom(fieldAtom);

    const conditionFieldError: string | undefined = useMemo(() => {
        return error?.yup.path === "condition" ? error.value : undefined;
    }, [error]);

    const titleRef = useRef<TextAreaRef | null>(null);

    useEffect(() => {
        setTimeout(() => {
            if (open && titleRef.current && !disabled) {
                titleRef.current.focus({ cursor: "all" });
            }
        }, 100);
    }, [open, titleRef, disabled]);

    const doIfValid = useCallback(
        (doCallback: () => void) => {
            validateForm(true)
                .then((res) => {
                    if (res.type === "ERROR") {
                        throw new Error("Local fields are invalid");
                    }
                    if (customFormRef.current) {
                        return customFormRef.current.validate(true);
                    } else {
                        throw new Error(
                            "Please use <EmptyCustomSection ref={ref} />"
                        );
                    }
                })
                .then((res) => {
                    if (res.type === "ERROR") {
                        throw new Error("Custom fields are invalid");
                    }
                })
                .then(() => {
                    doCallback();
                })
                .catch((e) => void e);
        },
        [validateForm, customFormRef]
    );

    const closeIfValid = useCallback(() => {
        doIfValid(onClose);
    }, [doIfValid, onClose]);

    const okButton = (
        <Button
            id="ok"
            onClick={closeIfValid}
            variant="primary"
            className={theme.buttonFooter}
        >
            {t("Confirm")}
        </Button>
    );

    let buttons = <></>;
    switch (type.type) {
        case "READ_ONLY": {
            buttons = okButton;
            break;
        }
        case "EDIT": {
            const { cannotDelete, cannotCopy } = getFieldTypeDrawerBehavior(
                value.fieldType
            );
            const copyIfValid = () => {
                doIfValid(() => {
                    if (conditionFieldError) {
                        // Prevent closing if there is a condition error
                        // The error is caught in `doIfValid`
                        throw new Error(conditionFieldError);
                    } else {
                        setDrawerBusy(true);
                        type.onCopyField(state.fieldAtom).finally(() =>
                            setDrawerBusy(false)
                        );
                    }
                });
            };
            buttons = (
                <>
                    {okButton}
                    {!cannotDelete && (
                        <Button
                            id="delete"
                            onClick={() => type.onDeleteField(state.fieldAtom)}
                            variant="link"
                            className={theme.buttonFooter}
                        >
                            <Svg className={theme.icon} src={DeleteIcon} />
                            {t("FieldBuilder_Drawer_Delete_Field")}
                        </Button>
                    )}
                    {!cannotCopy && (
                        <Button
                            id="copy"
                            onClick={copyIfValid}
                            variant="link"
                            className={cx(
                                theme.buttonFooter,
                                theme.copyButtonSpacing
                            )}
                        >
                            <Svg className={theme.icon} src={CopyIcon} />
                            {t("FieldBuilder_Drawer_Copy_Field")}
                        </Button>
                    )}
                </>
            );
            break;
        }
        default: {
            return assertNever(type);
        }
    }

    return (
        <Drawer
            onCancel={disabled ? onClose : closeIfValid}
            buildHeader={() =>
                state ? renderFieldDrawerHeader(state.fieldAtom) : null
            }
            buildFooter={() => buttons}
            busy={drawerBusy}
            open={open}
        >
            <DrawerContentWrapper
                titleRef={titleRef}
                state={state}
                fieldAtoms={fieldAtoms}
                renderFieldDrawerCustomSection={renderFieldDrawerCustomSection}
                getFieldTypeConditionalValues={getFieldTypeConditionalValues}
                getFieldTypeDrawerBehavior={getFieldTypeDrawerBehavior}
                disabled={disabled}
                blobsAtom={blobsAtom}
                conditionFieldError={conditionFieldError}
                customFormRef={customFormRef}
                atoms={atoms}
            />
        </Drawer>
    );
}

export const FieldDrawer = memo(FieldDrawerNoMemo, (prevProps, nextProps) => {
    const isEq =
        prevProps.open === nextProps.open &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.type === nextProps.type &&
        Object.is(prevProps.state, nextProps.state);
    return isEq;
}) as typeof FieldDrawerNoMemo;

interface DrawerContentWrapperProps<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
> extends CommonDrawerProps<F, FT, FTN> {
    state: FieldDrawerState<F, FT, FTN>;
    fieldAtoms: Array<FormAtom<F>>;
    getFieldTypeDrawerBehavior: (fieldType: FT) => DrawerBehavior;
    conditionFieldError: string | undefined;
    customFormRef: CompoundFormForwardedRef;
    atoms: Atoms;
    titleRef: React.MutableRefObject<TextAreaRef | null>;
}
function DrawerContentWrapper<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
>({
    state,
    fieldAtoms,
    renderFieldDrawerCustomSection,
    getFieldTypeConditionalValues,
    getFieldTypeDrawerBehavior,
    disabled = false,
    blobsAtom,
    conditionFieldError,
    customFormRef,
    atoms,
    titleRef,
}: DrawerContentWrapperProps<F, FT, FTN>) {
    const field = useAtomValue(state.fieldAtom);

    const fieldAtomsBeforeAtom = useMemo(
        () =>
            createAtom((get) => {
                const result = [];
                for (const fa of fieldAtoms) {
                    if (fa === state.fieldAtom) {
                        break;
                    }
                    const f = get(fa);
                    const values = getFieldTypeConditionalValues(f.fieldType);
                    if (values.length > 0) {
                        result.push(fa);
                    }
                }
                return result;
            }),
        [state.fieldAtom, fieldAtoms, getFieldTypeConditionalValues]
    );
    const fieldAtomsBefore = useAtomValue(fieldAtomsBeforeAtom);

    const conditionFieldAtom = useMemo(
        () =>
            createAtom((get) => {
                const conditionFieldId = field.condition?.fieldId;
                if (conditionFieldId === undefined) {
                    return null;
                }
                for (const fa of fieldAtoms) {
                    const f = get(fa);
                    if (f.id === conditionFieldId) {
                        return f;
                    }
                }
                return null;
            }),
        [field, fieldAtoms]
    );

    const drawerBehavior = getFieldTypeDrawerBehavior(field.fieldType);

    const {
        nameAtom,
        instructionsAtom,
        conditionAtom,
        requiredAtom,
        blobIdsAtom,
    } = atoms;
    const { fieldAtom } = state;

    const blobDataAtom = useMemo(
        () =>
            atom(
                (get) => {
                    const blobIds = get(blobIdsAtom);
                    const blobs = get(blobsAtom);
                    return {
                        blobIds,
                        blobs,
                    };
                },
                (get, set, newBlobData: SetStateAction<BlobData>) => {
                    const update =
                        newBlobData instanceof Function
                            ? newBlobData({
                                  blobIds: get(blobIdsAtom),
                                  blobs: get(blobsAtom),
                              })
                            : newBlobData;
                    set(blobIdsAtom, update.blobIds);
                    set(blobsAtom, update.blobs);
                }
            ),
        [blobIdsAtom, blobsAtom]
    );

    const blobDataFormAtom = useMemo(
        () =>
            formAtom(blobDataAtom, Yup.mixed<BlobData>().defined(), fieldAtom),
        [blobDataAtom, fieldAtom]
    );

    const uploaderFilesAtom = useMemo(
        () =>
            mapBlobDataToUploaderFiles(
                blobDataFormAtom,
                t("File_Upload_Validation_Error")
            ),
        [blobDataFormAtom]
    );

    const fieldsBeforeAtom = useMemo(
        () =>
            createAtom((get) => {
                const fields = fieldAtomsBefore.map((fa) => get(fa));
                return fields;
            }),
        [fieldAtomsBefore]
    );
    const fieldsBefore = useAtomValue(fieldsBeforeAtom);

    const conditionField = useAtomValue(conditionFieldAtom);

    const styleVariant = disabled ? "borderless" : "normal";

    return (
        <>
            <FAStringInput
                label={t("FieldBuilder_Drawer_Field_Name")}
                disabled={disabled}
                atom={nameAtom}
                styleVariant={styleVariant}
                ref={titleRef}
                autoFocus
            />
            <FAMarkdownInput
                placeholder={t(
                    "FieldBuilder_Drawer_Field_Instructions_Placeholder"
                )}
                label={t("FieldBuilder_Drawer_Field_Instructions")}
                disabled={disabled}
                atom={instructionsAtom}
                styleVariant={styleVariant}
            />
            <FAFileUploaderInput
                disabled={disabled}
                atom={uploaderFilesAtom}
                emptyText={t("EmptyAttachment_Label")}
                label={t("Attached_Files")}
                deleteAction={{
                    kind: "DELETE_BLOB",
                }}
                maxFiles={CONSTANTS.COMMON_CONSTANTS.MAX_BLOBS_PER_FIELD}
                hideMissing
            />
            {renderFieldDrawerCustomSection(fieldAtom, customFormRef)}
            {!drawerBehavior.hideCondition && (
                <>
                    <FieldDrawerSeparator />
                    <ConditionEditor
                        atom={conditionAtom}
                        fieldsBefore={fieldsBefore}
                        conditionField={conditionField}
                        fieldError={conditionFieldError}
                        getFieldTypeConditionalValues={
                            getFieldTypeConditionalValues
                        }
                        disabled={disabled}
                    />
                </>
            )}
            {!drawerBehavior.hideRequired && (
                <>
                    <FieldDrawerSeparator />
                    <FACheckboxInput
                        label={t("FieldBuilder_Drawer_Field_Required")}
                        atom={requiredAtom}
                        disabled={disabled}
                    />
                </>
            )}
        </>
    );
}
